/* ============================================================
   dashboard.js — paints an animated enterprise dashboard onto a
   2D canvas, used as the browser window's screen CanvasTexture.

   Light "web-app" theme so it reads clearly as a real screen inside
   the dark 3D browser frame. Brand greens kept as accents.

   Pure drawing helpers (no three.js here). drawDashboard() is called
   on a throttled cadence (~15-20fps) by PlatformViz, with a
   monotonically increasing `time` (seconds) driving the animation.
   ============================================================ */

// Dark app palette (brand greens kept as bright accents) — see reference image:
// deep navy canvas, slightly raised navy cards, near-white text, soft slate labels.
const COL = {
  bg1: "#0d1623",
  bg2: "#0a111c",
  card: "#16202f",
  stroke: "rgba(255,255,255,0.08)",
  text: "#e8eef6",
  textSoft: "#8595ab",
  green: "#1ec77b",
  teal: "#17a98c",
  mint: "#27d391",
};

const TAU = Math.PI * 2;

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// smooth pseudo-random series so charts look organic but stable
function wave(seed, t, freq, amp) {
  return (
    Math.sin(t * freq + seed * 1.7) * amp +
    Math.sin(t * freq * 0.5 + seed * 3.1) * amp * 0.5
  );
}

// white card with soft shadow + hairline border
function drawCard(ctx, x, y, w, h, title) {
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 4;
  roundRect(ctx, x, y, w, h, 14);
  ctx.fillStyle = COL.card;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x, y, w, h, 14);
  ctx.lineWidth = 1;
  ctx.strokeStyle = COL.stroke;
  ctx.stroke();
  if (title) {
    ctx.fillStyle = COL.textSoft;
    ctx.font = "600 15px 'Segoe UI', Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(title, x + 16, y + 26);
  }
}

function drawKpi(ctx, x, y, w, h, label, value, suffix, accent, t, seed) {
  drawCard(ctx, x, y, w, h);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  // label
  ctx.fillStyle = COL.textSoft;
  ctx.font = "600 13px 'Segoe UI', Arial, sans-serif";
  ctx.fillText(label, x + 16, y + 28);

  // animated counting value
  const ease = 0.5 - 0.5 * Math.cos(Math.min(1, t * 0.5) * Math.PI);
  const shown = value * (0.85 + 0.15 * (0.5 + 0.5 * Math.sin(t * 0.8 + seed)));
  ctx.fillStyle = COL.text;
  ctx.font = "700 30px 'Segoe UI', Arial, sans-serif";
  const num = suffix === "$" ? `$${Math.round(shown * ease).toLocaleString()}` : `${Math.round(shown * ease).toLocaleString()}${suffix}`;
  ctx.fillText(num, x + 16, y + 62);

  // delta chip
  const up = wave(seed, t, 0.6, 1) >= 0;
  ctx.fillStyle = up ? COL.green : "#e0556f";
  ctx.font = "600 12px 'Segoe UI', Arial, sans-serif";
  ctx.fillText(`${up ? "▲" : "▼"} ${(2 + Math.abs(wave(seed, t, 0.9, 6))).toFixed(1)}%`, x + 16, y + h - 16);

  // mini sparkline
  const sx = x + w * 0.46;
  const sw = w * 0.46;
  const sy = y + h - 22;
  const sh = 34;
  ctx.beginPath();
  for (let i = 0; i <= 16; i += 1) {
    const px = sx + (sw * i) / 16;
    const py = sy - (0.5 + 0.5 * Math.sin(i * 0.7 + t * 1.2 + seed)) * sh;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawAreaChart(ctx, x, y, w, h, t) {
  drawCard(ctx, x, y, w, h, "Revenue Analytics");
  const cx = x + 16;
  const cy = y + 44;
  const cw = w - 32;
  const ch = h - 64;

  // gridlines
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i += 1) {
    const gy = cy + (ch * i) / 4;
    ctx.beginPath();
    ctx.moveTo(cx, gy);
    ctx.lineTo(cx + cw, gy);
    ctx.stroke();
  }

  const N = 40;
  const pts = [];
  const progress = 0.15 + 0.85 * (0.5 + 0.5 * Math.sin(t * 0.25));
  const lastIdx = Math.max(2, Math.floor(N * progress));
  for (let i = 0; i <= lastIdx; i += 1) {
    const px = cx + (cw * i) / N;
    const base = 0.5 + 0.32 * Math.sin(i * 0.28 + t * 0.6) + 0.12 * Math.sin(i * 0.7 - t);
    const py = cy + ch - base * ch;
    pts.push([px, py]);
  }

  // area fill
  const grad = ctx.createLinearGradient(0, cy, 0, cy + ch);
  grad.addColorStop(0, "rgba(11,171,100,0.22)");
  grad.addColorStop(1, "rgba(11,171,100,0.0)");
  ctx.beginPath();
  ctx.moveTo(pts[0][0], cy + ch);
  pts.forEach((p) => ctx.lineTo(p[0], p[1]));
  ctx.lineTo(pts[pts.length - 1][0], cy + ch);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // line
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
  ctx.strokeStyle = COL.green;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // moving highlight dot at the sweep head
  const head = pts[pts.length - 1];
  ctx.beginPath();
  ctx.arc(head[0], head[1], 4.5, 0, TAU);
  ctx.fillStyle = COL.green;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(head[0], head[1], 9, 0, TAU);
  ctx.strokeStyle = "rgba(11,171,100,0.4)";
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawBars(ctx, x, y, w, h, t) {
  drawCard(ctx, x, y, w, h, "Operations");
  const cx = x + 16;
  const cy = y + 44;
  const cw = w - 32;
  const ch = h - 60;
  const n = 7;
  const gap = 10;
  const bw = (cw - gap * (n - 1)) / n;
  for (let i = 0; i < n; i += 1) {
    const val = 0.35 + 0.6 * (0.5 + 0.5 * Math.sin(i * 0.9 + t * 1.1));
    const bh = val * ch;
    const bx = cx + i * (bw + gap);
    const by = cy + ch - bh;
    const g = ctx.createLinearGradient(0, by, 0, by + bh);
    g.addColorStop(0, COL.mint);
    g.addColorStop(1, COL.teal);
    roundRect(ctx, bx, by, bw, bh, 4);
    ctx.fillStyle = g;
    ctx.fill();
  }
}

function drawTable(ctx, x, y, w, h, t) {
  drawCard(ctx, x, y, w, h, "Recent Transactions");
  const rows = ["Invoice #1042", "Payroll Run", "PO #774", "Stock Adjust", "Sales Order"];
  const status = ["Posted", "Approved", "Pending", "Posted", "Approved"];
  const ry = y + 46;
  const rh = (h - 58) / rows.length;
  for (let i = 0; i < rows.length; i += 1) {
    const yy = ry + i * rh;
    const active = Math.floor(t * 0.8) % rows.length === i;
    if (active) {
      roundRect(ctx, x + 8, yy - rh * 0.5 + 4, w - 16, rh - 6, 6);
      ctx.fillStyle = "rgba(30,199,123,0.12)";
      ctx.fill();
    }
    ctx.fillStyle = COL.text;
    ctx.font = "500 13px 'Segoe UI', Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(rows[i], x + 16, yy + 4);

    const st = status[i];
    const stCol = st === "Pending" ? "#c79a16" : st === "Approved" ? COL.green : COL.teal;
    ctx.textAlign = "right";
    ctx.fillStyle = stCol;
    ctx.font = "600 12px 'Segoe UI', Arial, sans-serif";
    ctx.fillText(st, x + w - 16, yy + 4);
  }
}

function drawActivity(ctx, x, y, w, h, t) {
  drawCard(ctx, x, y, w, h, "Live Activity");
  const dots = 4;
  const labels = ["Sync complete", "New order", "Report ready", "User login"];
  for (let i = 0; i < dots; i += 1) {
    const yy = y + 52 + i * 26;
    const blink = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * 3 + i * 1.3));
    ctx.beginPath();
    ctx.arc(x + 18, yy, 5, 0, TAU);
    ctx.fillStyle = `rgba(11,171,100,${blink})`;
    ctx.fill();
    ctx.fillStyle = COL.textSoft;
    ctx.font = "500 12px 'Segoe UI', Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(labels[i], x + 34, yy + 4);
  }
}

/**
 * Paint the full dashboard. ctx must be a 2D context sized to (w,h).
 * `time` is elapsed seconds (drives all animation).
 */
export function drawDashboard(ctx, w, h, time) {
  // light app background
  const bg = ctx.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, COL.bg1);
  bg.addColorStop(1, COL.bg2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const pad = 22;
  // ---- header bar ----
  ctx.fillStyle = COL.text;
  ctx.font = "700 22px 'Segoe UI', Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Enterprise Dashboard", pad, pad + 24);
  ctx.fillStyle = COL.textSoft;
  ctx.font = "500 14px 'Segoe UI', Arial, sans-serif";
  ctx.fillText("Sales · Operations · Finance — unified", pad, pad + 46);
  // status pill (top-right)
  const pill = "● All systems operational";
  ctx.font = "600 13px 'Segoe UI', Arial, sans-serif";
  const pw = ctx.measureText(pill).width + 26;
  roundRect(ctx, w - pad - pw, pad + 6, pw, 28, 14);
  ctx.fillStyle = "rgba(11,171,100,0.12)";
  ctx.fill();
  ctx.fillStyle = COL.green;
  ctx.textAlign = "left";
  ctx.fillText(pill, w - pad - pw + 13, pad + 25);

  const top = pad + 64;
  const colGap = 18;

  // ---- KPI row (4 cards) ----
  const kpiH = 104;
  const kpiW = (w - pad * 2 - colGap * 3) / 4;
  const kpis = [
    ["Revenue", 184230, "$", COL.green],
    ["Orders", 1280, "", COL.teal],
    ["Active Users", 342, "", COL.mint],
    ["Margin", 38, "%", COL.green],
  ];
  kpis.forEach((k, i) => {
    drawKpi(ctx, pad + i * (kpiW + colGap), top, kpiW, kpiH, k[0], k[1], k[2], k[3], time, i + 1);
  });

  // ---- main grid: area chart (left 2/3) + activity (right 1/3) ----
  const gridTop = top + kpiH + colGap;
  const gridH = h - gridTop - pad;
  const rightW = (w - pad * 2) * 0.3;
  const leftW = w - pad * 2 - rightW - colGap;

  const chartH = gridH * 0.56;
  drawAreaChart(ctx, pad, gridTop, leftW, chartH, time);

  // bottom-left: bars + table side by side
  const blTop = gridTop + chartH + colGap;
  const blH = gridH - chartH - colGap;
  const barsW = leftW * 0.42;
  drawBars(ctx, pad, blTop, barsW, blH, time);
  drawTable(ctx, pad + barsW + colGap, blTop, leftW - barsW - colGap, blH, time);

  // right column: activity (full height)
  drawActivity(ctx, pad + leftW + colGap, gridTop, rightW, gridH, time);
}

export { COL as DASHBOARD_COLORS };
